import { DialogContent, Box } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { DropdownItem } from './components/DropdownItem';
import { WidgetsAdditionalOptions } from '../WidgetsAdditionalOptions';
import { DosingPumpIcon } from 'src/assets/icons/DosingPumpIcon';
import { ValveWidget } from 'src/assets/widgets';
import { SelectedWidgetIcon } from './components/SelectedWidgetIcon';
import { useAuthentication } from 'src/features/authentication/context';
import {
  useGetAllTagsQuery,
  useGetAllAssetsQuery,
  useGetAllControllersQuery,
} from 'src/hooks/reactQueryApiHooks';
import { TagWithLabel } from './components/TagWithLabel';
import { UseFieldArrayAppend, useFormContext } from 'react-hook-form';
import { FormValuesTypes } from './types';
import { RHFSwitch } from 'src/components/minimals/form';
import { useResponsive } from 'src/hooks/useResponsive';
import { WidgetSubTypeEnum } from 'src/features/sites/types/types';
import { AssetType } from 'src/types/assets';
import { CustomWaterLoading } from 'src/components';
import { ValveScadaIcon } from 'src/assets/icons/SCADA/assetsComponents/ValveScadaIcon';
import { WetWellScadaIcon } from 'src/assets/icons/SCADA/assetsComponents/WetWellScadaIcon';
import { AssetTypes } from 'src/features/asset-details/types/asset';
import { RegularPumpWithRotor } from 'src/assets/icons/SCADA/assetsComponents/RegularPumpWithRotor';
import { SpinningPumpScadaIcon } from 'src/assets/icons/SCADA/assetsComponents/SpinningPumpScadaIcon';
import { TagResponse } from 'src/types';
import { ValveScreenIcon1 } from 'src/assets/widgets/ValveScreenIcon1';
import { ValveScreenIcon2 } from 'src/assets/widgets/ValveScreenIcon2';

type Props = {
  fields: { label: string; tagId: string; id: string }[];
  remove: (index: number) => void;
  append: UseFieldArrayAppend<FormValuesTypes, 'tags'>;
  assetType: string;
};

export const DialogContentElement = ({ fields, remove, append, assetType }: Props) => {
  const fieldSize = 'small';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const subTypes = [
    { id: '1', icon: RegularPumpWithRotor, name: WidgetSubTypeEnum.PUMP_1, assetType: 'PUMP' },
    { id: '2', icon: DosingPumpIcon, name: WidgetSubTypeEnum.PUMP_2, assetType: 'PUMP' },
    { id: '3', icon: SpinningPumpScadaIcon, name: WidgetSubTypeEnum.PUMP_3, assetType: 'PUMP' },
    { id: '4', icon: ValveWidget, name: WidgetSubTypeEnum.VALVE, assetType: 'VALVE' },
    { id: '5', icon: ValveScadaIcon, name: WidgetSubTypeEnum.VALVE_1, assetType: 'VALVE' },
    { id: '6', icon: ValveScreenIcon1, name: WidgetSubTypeEnum.VALVE_2, assetType: 'VALVE' },
    { id: '7', icon: ValveScreenIcon2, name: WidgetSubTypeEnum.VALVE_3, assetType: 'VALVE' },
    { id: '8', icon: WetWellScadaIcon, name: WidgetSubTypeEnum.WET_WELL_1, assetType: 'WET_WELL' },
  ];
  const { setValue, getValues } = useFormContext();

  const filteredSubTypes = useMemo(() => {
    if (!assetType) return [];

    return subTypes?.filter((s) => s.assetType === assetType);
  }, [assetType, subTypes]);

  useEffect(() => {
    if (!filteredSubTypes.length) return;

    const currentSubType = getValues('widgetSubType');

    if (!currentSubType) {
      setValue('widgetSubType', filteredSubTypes[0].name, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [filteredSubTypes, getValues, setValue]);

  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const { tags } = useGetAllTagsQuery({ customerId: customerId!, siteId: siteId! });
  const { assets } = useGetAllAssetsQuery({ customerId: customerId!, siteId: siteId! });
  const { controllers } = useGetAllControllersQuery({ customerId: customerId!, siteId: siteId! });
  const visibleTags = tags?.filter((tag: TagResponse) => !tag.hidden);

  const allowedAssetTypes = [AssetTypes.PUMP, AssetTypes.VALVE, AssetTypes.WET_WELL];
  const filteredAssets = assets?.filter((asset) =>
    allowedAssetTypes.includes(asset.assetType as AssetTypes)
  );

  const isMobile = useResponsive('down', 'sm');

  const areTagsReady = Array.isArray(visibleTags) && visibleTags?.length > 0;
  return (
    <>
      <DialogContent sx={{ width: isMobile ? '100%' : '650px', maxWidth: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: isMobile ? '0px' : '80px',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Box
            sx={{
              width: '300px',
              maxWidth: '100%',
              display: 'flex',
              gap: '15px',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              marginBottom: '20px',
            }}
          >
            <DropdownItem
              formName="assetId"
              title="Asset"
              label="Select Asset"
              options={filteredAssets ?? []}
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  const found = filteredAssets?.find((a) => a.id === option);
                  return found?.assetName ?? option;
                }
                return option.assetName;
              }}
              size={fieldSize}
              onChange={(asset: AssetType) => {
                if (!asset?.assetType) return;

                const subType = subTypes?.find((s) => s.assetType === asset.assetType);

                // if (!subType) return;
                setValue('widgetSubType', subType?.name, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
            />
            <DropdownItem
              formName="controllerId"
              title="Controller"
              label="Select Controller"
              options={controllers ?? []}
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  const found = controllers?.find((c) => c.id === option);
                  return found?.controllerName ?? option;
                }
                return option.controllerName;
              }}
              size={fieldSize}
            />
            <Box sx={{ display: 'flex', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
              <RHFSwitch
                name="showAlarmStatus"
                testId="show-alarm-status"
                label="Show Alarm Status"
                handleChange={(_, checked) => setValue('showAlarmStatus', checked)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              border: '0.3px solid grey',
              borderRadius: '8px',
              padding: '1rem',
              width: '120px',
              height: '120px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Box sx={{ position: 'absolute', top: '-20px', left: '-20px', cursor: 'pointer' }}>
              <WidgetsAdditionalOptions subTypes={filteredSubTypes} />
            </Box>
            {filteredSubTypes?.length > 0 && (
              <SelectedWidgetIcon
                subTypes={filteredSubTypes}
                widgetIcon={filteredSubTypes[0].icon}
              />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '10px' : '5px',
            marginTop: isMobile ? '20px' : 0,
          }}
        >
          {!areTagsReady ? (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <CustomWaterLoading />
            </Box>
          ) : (
            fields.map((field, index) => (
              <TagWithLabel
                key={field.id}
                tags={visibleTags}
                index={index}
                onRemove={() => remove(index)}
                isMobile={isMobile}
                // disableRemove={fields.length === 1}
              />
            ))
          )}
        </Box>
      </DialogContent>
    </>
  );
};
